import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState } from 'react';
import { SharedDriveModel } from '../../../../../../../types';
import { UserKeys, deleteSharedDrive } from '../../../../../../api/users-api';
import ChangeLogDialog from '../../../../../../shared/change-log-dialog';
import SharedDialog from '../../../../../../shared/shared-dialog';
import { handleErrors } from '../../../../../../utils/errors';
import { useStore } from '../../../../../state/zustand-store';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../../../utils/roles/client-roles';
import { useAnalytics } from '../../../../../../shared/analytics/create-events';
import { ModuleList } from '../../../../../../shared/analytics/module-list';
import * as analytics from '../../../../../../shared/analytics/analytics.json';
import { useUserSettings } from '../../../../../../utils/use-user-settings';
import { Messages } from '../../../../../../utils/message-constants';
import { isFeatureEnabled } from '../../../../../../utils/feature-flags';
import Typography from '@mui/material/Typography';
interface SharedDrivesDeleteDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDrive: SharedDriveModel;
  setSelectedDrive: Dispatch<SetStateAction<SharedDriveModel | null>>;
  changeLogRoles: string[];
}

export default function SharedDrivesDeleteDialog({
  open,
  handleClose,
  changeLogRoles,
  selectedDrive,
  setSelectedDrive,
}: SharedDrivesDeleteDialogProps) {
  const { createEvent } = useAnalytics();
  const { features, loggedInUser, adminEmail, currentTier } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const url = window.location.href.toLowerCase();
  const isSharedDrives = url.includes('shared');
  const isDriveExplorer = url.includes('explorer');

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteSharedDrive(selectedDrive.id, loggedInUser, true, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: currentTier === 'enterprise' ? 'success' : 'info',
        message:
          currentTier === 'enterprise'
            ? Messages.Success.Deleted('Shared drive')
            : 'Shared Drive is currently being deleted.',
      });

      function updateDriveData(data: SharedDriveModel[] | undefined) {
        if (!data) return;
        return data.filter((drive) => drive.id !== selectedDrive.id);
      }

      if (currentTier === 'enterprise') {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.SharedDrivesIndexedPage],
        });
      } else if (!isFeatureEnabled('showPaginatedSharedDrives', features)) {
        queryClient.setQueryData(
          [UserKeys.SharedDrivesForDomain],
          updateDriveData
        );
      } else {
        queryClient.setQueryData(
          [UserKeys.SharedDrivesForDomainPage],
          updateDriveData
        );
      }

      const queries = queryClient.getQueriesData<SharedDriveModel[]>({
        queryKey: [UserKeys.SharedDrivesForUser],
      });

      queries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, updateDriveData(data));
      });

      setChangeLogMessage('');
      setSelectedDrive(null);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('the shared drive'),
      });
    },
    onSettled: () => {
      if (isSharedDrives) {
        createEvent(analytics.drive.drive_explorer.shared_drives.delete_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.sharedDrives,
        });
      } else if (isDriveExplorer) {
        createEvent(analytics.drive.drive_explorer.shared_drives.delete_drive, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.shared_drives
            .delete_drive,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }

      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DriveRoles.CanDeleteSharedDrives.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setSelectedDrive(null);
        handleClose();
      }}
      title="Delete Shared Drive"
      actingAs={adminEmail}
      dialogContent={
        <>
          <Typography>
            Delete <strong>{selectedDrive.name}</strong>?
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Warning: This will permanently delete the shared drive and all of
            its contents. This action cannot be undone.
          </Typography>
          {currentTier !== 'enterprise' ? (
            <Typography>
              The drive may take up to a minute to be completely deleted.
            </Typography>
          ) : null}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Delete Shared Drive',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
